import { FC } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge, TooltipCard } from '@components';
import { useTrans } from '@libs/i18n';
import { CaretRightIcon, CheckIcon, XIcon } from '@phosphor-icons/react';

import type { TenantProfileDocumentDto } from '../../api/types';
import { deriveDisplayStatus } from '../hub/documentStatusMeta';
import { DocumentStatusPill } from '../hub/DocumentStatusPill';
import { formatFileSize, formatUploadedAgo } from '../hub/hubFormat';

interface Props {
  fileName: string;
  doc?: TenantProfileDocumentDto;
  categoryName: string | null;
  readOnly: boolean;
  canSave: boolean;
  saving: boolean;
  /** Explanatory tooltip shown on the disabled Approve & save button. */
  saveDisabledReason?: string;
  onClose: () => void;
  onSave: () => void;
}

/**
 * Sticky review header: a breadcrumb (Document Hub › Review extraction), the
 * document title with its status + category, the file subline, and the Close /
 * Approve & save actions. The save button stays disabled (with a tooltip) until
 * every low-confidence field is resolved.
 */
export const ReviewHeader: FC<Props> = observer(
  ({
    fileName,
    doc,
    categoryName,
    readOnly,
    canSave,
    saving,
    saveDisabledReason,
    onClose,
    onSave,
  }) => {
    const { t, i18n } = useTrans();
    const theme = useTheme();

    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: 'background.default',
          borderBottom: 1,
          borderColor: 'grey.300',
          py: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: { xs: 2, md: 4 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                sx={{
                  border: 0,
                  background: 'transparent',
                  p: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  typography: 'textSM_regular',
                  color: 'text.tertiary',
                }}
              >
                {t('app.documentHub.breadcrumb')}
              </Box>
              <CaretRightIcon size={12} color={theme.palette.text.disabled} />
              <Typography variant="textSM_semiBold" color="text.secondary">
                {t('app.documentHub.review.breadcrumb')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography variant="textXL_bold" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                {fileName}
              </Typography>
              {doc && <DocumentStatusPill status={deriveDisplayStatus(doc)} />}
              {categoryName && <Badge variant="DomainName" label={categoryName} />}
            </Box>

            {doc && (
              <Typography variant="textXS_regular" color="text.tertiary">
                {formatFileSize(doc.fileSize)}
                {doc.classificationMetadata.pageCount
                  ? ` · ${t('app.documentHub.meta.pages', {
                      count: doc.classificationMetadata.pageCount,
                    })}`
                  : ''}
                {` · ${t('app.documentHub.meta.uploaded', {
                  when: formatUploadedAgo(doc.uploadedAt, i18n.language),
                })}`}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none' }}>
            <Button
              variant="tertiary"
              startIcon={<XIcon size={16} />}
              onClick={onClose}
              sx={{ textTransform: 'none' }}
            >
              {t('app.documentHub.review.close')}
            </Button>
            {!readOnly && (
              <TooltipCard variant="dark" description={!canSave && saveDisabledReason ? saveDisabledReason : ''}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon size={16} />}
                    disabled={!canSave}
                    onClick={onSave}
                    sx={{
                      textTransform: 'none',
                      // The global MuiButton root override forces a dark
                      // background, masking `color="success"` — pin the green here.
                      backgroundColor: 'success.main',
                      color: 'common.white',
                      '&:hover': { backgroundColor: 'success.dark' },
                      '&.Mui-disabled': {
                        backgroundColor: 'action.disabledBackground',
                        color: 'action.disabled',
                      },
                    }}
                  >
                    {saving
                      ? t('app.documentHub.review.saving')
                      : t('app.documentHub.review.approveSave')}
                  </Button>
                </span>
              </TooltipCard>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);
